import { useState, useMemo, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { PencilSimple, Trash, Printer, CaretLeft, CaretRight, MagnifyingGlass, Globe, CheckCircle } from "@phosphor-icons/react";
import { bookingTotal } from "../pages/bookingFields";
import { StatusPill, EmptyState } from "./lux";

const PAGE_SIZE = 50;

const fmtWindow = (s) =>
  s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

export function BookingTable({ rows, loading, testid, onEdit, onDelete, actions, onQuote, onRowClick, rowMarker, selectable, bulkActions }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState({});

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((b) =>
      [b.customer_name, b.vehicle_label, b.vehicle_plate, b.salesperson]
        .some((v) => String(v || "").toLowerCase().includes(s))
    );
  }, [rows, q]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > pageCount - 1) setPage(0); }, [pageCount, page]);
  const pageRows = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const showSearch = rows.length > 8;
  const showPager = rows.length > PAGE_SIZE;

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const clearSelection = () => setSelected({});
  const allPageSelected = pageRows.length > 0 && pageRows.every((b) => selected[b.id]);
  const toggleAllPage = () => {
    const next = { ...selected };
    if (allPageSelected) pageRows.forEach((b) => { delete next[b.id]; });
    else pageRows.forEach((b) => { next[b.id] = true; });
    setSelected(next);
  };
  const colSpan = selectable ? 9 : 8;

  if (loading) return <div className="p-12 text-center text-muted-foreground border border-border bg-card">Loading…</div>;
  if (rows.length === 0) return (
    <div className="border border-border bg-card">
      <EmptyState icon={CheckCircle} title="Nothing in this pipeline" hint="Bookings will appear here as they move through the workflow." />
    </div>
  );
  return (
    <div className="space-y-3">
      {selectable && selectedIds.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3 border border-primary/40 bg-primary/10 rounded-2xl px-5 py-3" data-testid={`bulk-bar-${testid}`}>
          <span className="text-sm font-medium tnum" data-testid={`bulk-count-${testid}`}>{selectedIds.length} selected</span>
          <div className="flex items-center gap-2">
            {bulkActions?.(selectedIds, clearSelection)}
            <Button size="sm" variant="ghost" onClick={clearSelection} data-testid={`bulk-clear-${testid}`}>Clear</Button>
          </div>
        </div>
      )}
      {(showSearch || showPager) && (
        <div className="flex items-center justify-between flex-wrap gap-3" data-testid={`bookings-controls-${testid}`}>
          <div className="relative w-full sm:w-80">
            <MagnifyingGlass size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Search client, vehicle, plate, salesperson…" className="pl-10 h-10 rounded-full bg-white/[0.04] border-border" data-testid={`bookings-search-${testid}`} />
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="tnum" data-testid={`bookings-count-${testid}`}>{filtered.length.toLocaleString()} result{filtered.length === 1 ? "" : "s"}</span>
            {showPager && (
              <>
                <Button size="icon" variant="outline" className="rounded-full h-9 w-9" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid={`bookings-prev-${testid}`}><CaretLeft size={14} /></Button>
                <span className="tnum">{page + 1}/{pageCount}</span>
                <Button size="icon" variant="outline" className="rounded-full h-9 w-9" disabled={page >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} data-testid={`bookings-next-${testid}`}><CaretRight size={14} /></Button>
              </>
            )}
          </div>
        </div>
      )}
      <div className="border border-border bg-card overflow-x-auto" data-testid={`bookings-table-${testid}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {selectable && <th className="px-4 py-3.5 w-8"><input type="checkbox" className="accent-[hsl(var(--gold))]" checked={allPageSelected} onChange={toggleAllPage} data-testid={`bulk-select-all-${testid}`} /></th>}
              <th className="w-1.5" />
              <th className="text-left px-4 py-3.5 label-mono font-normal">Client</th>
              <th className="text-left px-4 py-3.5 label-mono font-normal">Vehicle</th>
              <th className="text-left px-4 py-3.5 label-mono font-normal">Plate</th>
              <th className="text-left px-4 py-3.5 label-mono font-normal">Period</th>
              <th className="text-right px-4 py-3.5 label-mono font-normal">Total</th>
              <th className="text-left px-4 py-3.5 label-mono font-normal">Salesperson</th>
              <th className="text-right px-4 py-3.5 label-mono font-normal w-72">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={colSpan} className="p-10 text-center text-muted-foreground">No bookings match “{q}”.</td></tr>
            ) : pageRows.map((b) => {
              const isInsurance = b.booking_type === "rental" && b.booking_category === "insurance";
              const total = bookingTotal(b);
              return (
                <tr key={b.id} onClick={() => onRowClick?.(b)} className={`border-b border-border/50 last:border-b-0 hover:bg-white/[0.03] transition-colors ${onRowClick ? "cursor-pointer" : ""}`} data-testid={`booking-row-${b.id}`}>
                  {selectable && (
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="accent-[hsl(var(--gold))]" checked={!!selected[b.id]} onChange={(e) => setSelected((p) => ({ ...p, [b.id]: e.target.checked }))} data-testid={`bulk-select-${b.id}`} />
                    </td>
                  )}
                  <td className="p-0">
                    <div className={`w-1 h-12 rounded-full ${isInsurance ? "bg-red-500" : b.booking_type === "rental" ? "bg-primary" : "bg-sky-500"}`} />
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-heading text-base flex items-center gap-2 flex-wrap">
                      {b.customer_name}
                      {b.source === "website" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-emerald-500/12 text-emerald-400 border border-emerald-500/25 font-sans" title="Submitted from the public Shopify booking form" data-testid={`website-badge-${b.id}`}>
                          <Globe size={10} /> Website
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground capitalize mt-0.5">
                      {b.booking_type}
                      {isInsurance && <span className="ml-2 text-red-400">· Insurance</span>}
                      {b.booking_type === "rental" && b.booking_category === "retail" && <span className="ml-2 text-primary">· Retail</span>}
                      {(b.booking_type === "chauffeur" || b.booking_type === "tour") && b.split_confirmed && (
                        <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-emerald-500/12 text-emerald-400 border border-emerald-500/25 normal-case" title={`Split confirmed${b.split_confirmed_at ? " on " + new Date(b.split_confirmed_at).toLocaleDateString() : ""}`} data-testid={`split-confirmed-tag-${b.id}`}>
                          <CheckCircle size={10} /> Split confirmed
                        </span>
                      )}
                    </div>
                    {rowMarker && rowMarker(b)}
                  </td>
                  <td className="px-4 py-4 text-muted-foreground">{b.vehicle_label || "—"}</td>
                  <td className="px-4 py-4 font-mono text-xs text-muted-foreground">{b.vehicle_plate || "—"}</td>
                  <td className="px-4 py-4 text-xs tnum text-muted-foreground whitespace-nowrap">
                    {fmtWindow(b.start_datetime)}
                    <br />→ {fmtWindow(b.end_datetime)}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="font-heading text-base money">${Number(total).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                    {b.tax_applied && <div className="text-[10px] text-muted-foreground">incl. HST</div>}
                  </td>
                  <td className="px-4 py-4 text-xs text-muted-foreground">{b.salesperson || "—"}</td>
                  <td className="px-4 py-4 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex gap-1.5 items-center">
                      {actions && actions(b)}
                      <Button size="sm" variant="ghost" className="rounded-full" onClick={() => onQuote(b)} title="Print/Quote" data-testid={`booking-quote-${b.id}`}><Printer size={14} /></Button>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => onEdit(b)} data-testid={`booking-edit-${b.id}`}><PencilSimple size={12} className="mr-1" />Edit</Button>
                      <Button size="sm" variant="ghost" className="rounded-full" onClick={() => onDelete(b.id)} data-testid={`booking-delete-${b.id}`}><Trash size={14} className="text-destructive" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
